<template>
  <!-- DsSheet (03_ДИЗАЙН_СИСТЕМА.md §6): a bottom sheet on a phone, a centred dialog from 700 px, or a floating panel on the
       right (variant="drawer"). Focus is trapped inside; Esc and a click on the backdrop close it. -->
  <Teleport to="#sheet-portal">
    <Transition name="sheet" appear>
      <div v-if="open" class="sheet-overlay" :class="`sheet-overlay--${variant}`" @click.self="$emit('close')">
        <div
          ref="panel"
          class="sheet"
          :class="[`sheet--${variant}`, `sheet--${size}`]"
          role="dialog"
          aria-modal="true"
          :aria-label="title"
          :style="{ maxHeight }"
          tabindex="-1"
          @click.stop
          @keydown="onKeydown"
        >
          <div class="sheet__grabber" />
          <button type="button" class="sheet__close" :aria-label="t('common.close')" data-testid="sheet-close" @click="$emit('close')">
            <IconClose :size="16" />
          </button>
          <div v-if="title || $slots.action" class="sheet__head">
            <h2 class="sheet__title">{{ title }}</h2>
            <div v-if="$slots.action" class="sheet__action">
              <slot name="action" />
            </div>
          </div>
          <div class="sheet__body hide-scrollbar">
            <slot />
          </div>
          <div v-if="$slots.footer" class="sheet__footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onUnmounted, ref, watch } from 'vue';
import { useStepUp } from '@/composables/useStepUp';
import { useI18n } from 'vue-i18n';
import IconClose from '../icons/IconClose.vue';

const props = withDefaults(defineProps<{
  open: boolean;
  title?: string;
  maxHeight?: string;
  /** auto — sheet on a phone, dialog from 700 px; drawer — a floating panel on the right from 700 px. */
  variant?: 'auto' | 'drawer';
  /** Dialog width from 700 px: m 430, l 480 (card sheets), filters 440. */
  size?: 'm' | 'l' | 'filters';
  /** Stay open when the tab is hidden — a step-up inside the sheet waits for a code the client reads in the mail app. */
  keepOnHide?: boolean;
}>(), { maxHeight: '85vh', variant: 'auto', size: 'm', keepOnHide: false });

const emit = defineEmits<{ close: [] }>();
const { t } = useI18n();
const panel = ref<HTMLElement | null>(null);
const { state: stepUp } = useStepUp();
let returnFocus: HTMLElement | null = null;

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') { e.stopPropagation(); emit('close'); return; }
  if (e.key !== 'Tab' || !panel.value) return;
  const items = Array.from(panel.value.querySelectorAll<HTMLElement>(FOCUSABLE));
  if (items.length === 0) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
}

// The prototype closes a sheet when the tab is hidden — a revealed card number must not wait for the client's return.
// Not while a step-up is open: the client leaves for the mail app to read the code and must come back to the same sheet.
function onVisibility() {
  if (document.visibilityState === 'hidden' && props.open && !stepUp.open && !props.keepOnHide) emit('close');
}

watch(() => props.open, async (open) => {
  if (typeof document === 'undefined') return;
  document.body.style.overflow = open ? 'hidden' : '';
  if (open) {
    returnFocus = document.activeElement as HTMLElement | null;
    document.addEventListener('visibilitychange', onVisibility);
    await nextTick();
    panel.value?.focus();
  } else {
    document.removeEventListener('visibilitychange', onVisibility);
    returnFocus?.focus?.();
    returnFocus = null;
  }
}, { immediate: true });

onUnmounted(() => {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = '';
    document.removeEventListener('visibilitychange', onVisibility);
  }
});
</script>

<style scoped lang="scss">
.sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: var(--scrim);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.sheet {
  position: relative;
  width: 100%;
  max-width: 560px;
  background: var(--ds-surface);
  border-top-left-radius: var(--radius-sheet);
  border-top-right-radius: var(--radius-sheet);
  box-shadow: var(--shadow-modal);
  display: flex;
  flex-direction: column;
  padding-bottom: max(30px, env(safe-area-inset-bottom));
  outline: none;
}

.sheet__grabber {
  width: 30px;
  height: 3px;
  border-radius: 2px;
  background: var(--line);
  margin: 10px auto 0;
}

.sheet__close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  border: 1px solid var(--line);
  color: var(--text-2);
  background: var(--ds-surface);
  z-index: 1;
}

.sheet__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 18px 60px 6px 24px;
}

.sheet__title {
  margin: 0;
  font-family: var(--font-heading);
  font-size: 19px;
  font-weight: 400;
  color: var(--text);
  letter-spacing: -0.01em;
  line-height: 1.2;
}
/* 03 §3.2: a sheet title is 19 px; the card sheets («Управление картой», «Данные карты») and the filters are 28 / 30. */
.sheet--l .sheet__title,
.sheet--filters .sheet__title,
.sheet--drawer .sheet__title { font-size: 28px; }

.sheet__body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 10px 24px 8px;
}

.sheet__footer {
  padding: 12px 24px 4px;
}

/* From 700 px: a centred dialog, or a floating panel on the right for the card details. */
@media (min-width: 700px) {
  .sheet-overlay--auto { align-items: center; }
  .sheet--auto {
    border-radius: 23px;
    padding-bottom: 22px;
    max-width: var(--dialog-w);
    &.sheet--l { max-width: var(--dialog-w-l); border-radius: 22px; }
    &.sheet--filters { max-width: var(--dialog-w-m); }
    .sheet__grabber { display: none; }
    &.sheet--l .sheet__title, &.sheet--filters .sheet__title { font-size: 30px; }
  }
  .sheet-overlay--drawer { align-items: stretch; justify-content: flex-end; padding: var(--drawer-inset); }
  .sheet--drawer {
    max-width: var(--drawer-w);
    border-radius: 20px;
    max-height: none !important;
    padding-bottom: 22px;
    .sheet__grabber { display: none; }
    .sheet__title { font-size: 22px; }          /* the side panel's title is small (b-desktop-modal-card-details-revealed) */
  }
}

/* Transitions — the sheet slides up on a phone; the dialog fades and lifts slightly; the panel slides in from the right. */
.sheet-enter-active .sheet,
.sheet-leave-active .sheet { transition: transform var(--dur-med) var(--ease-out), opacity var(--dur-med) var(--ease-out); }
.sheet-enter-from .sheet,
.sheet-leave-to .sheet { transform: translateY(100%); }
@media (min-width: 700px) {
  .sheet-enter-from .sheet--auto,
  .sheet-leave-to .sheet--auto { transform: translateY(12px); opacity: 0; }
  .sheet-enter-from .sheet--drawer,
  .sheet-leave-to .sheet--drawer { transform: translateX(24px); opacity: 0; }
}
.sheet-enter-active,
.sheet-leave-active { transition: opacity 200ms ease-out; }
.sheet-enter-from,
.sheet-leave-to { opacity: 0; }
</style>
